import { api, handleApiResponse, handleApiError } from '@/lib/api';
import { isAxiosError } from 'axios';
import { 
  Titulo, 
  CreateTituloRequest, 
  UpdateTituloRequest,
  FilterQuery,
  PaginationQuery,
  PaginatedResponse,
  KanbanBoardResponse,
  Estatisticas,
  DistribuicaoOperador,
  ExternalImportResult,
  ExternalApiConfig,
  ExternalApiFullConfig,
  ExternalApiJob,
  ConnectionTestResult
} from '@/types';

export const tituloService = {
  async getAll(filters: FilterQuery = {}, pagination: PaginationQuery = {}): Promise<PaginatedResponse<Titulo>> {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });

      // Add pagination
      if (pagination.page) params.append('page', String(pagination.page));
      if (pagination.limit) params.append('limit', String(pagination.limit));
      if (pagination.sortBy) params.append('sortBy', pagination.sortBy);
      if (pagination.sortOrder) params.append('sortOrder', pagination.sortOrder);

      const response = await api.get(`/titulos?${params.toString()}`);
      return {
        data: response.data.data,
        pagination: response.data.pagination,
      };
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getById(id: string): Promise<Titulo> {
    try {
      const response = await api.get(`/titulos/${id}`);
      return (handleApiResponse(response) as any).titulo;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async create(data: CreateTituloRequest): Promise<Titulo> {
    try {
      const response = await api.post('/titulos', data);
      return (handleApiResponse(response) as any).titulo;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async update(id: string, data: UpdateTituloRequest): Promise<Titulo> {
    try {
      const response = await api.put(`/titulos/${id}`, data);
      return (handleApiResponse(response) as any).titulo;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await api.delete(`/titulos/${id}`);
    } catch (error) {
      handleApiError(error);
    }
  },

  async getKanban(filters: FilterQuery = {}): Promise<KanbanBoardResponse> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await api.get(`/titulos/kanban?${params.toString()}`);
      return handleApiResponse(response) as any as KanbanBoardResponse;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async updateKanbanEtapa(id: string, kanbanEtapaId: string | null): Promise<Titulo> {
    try {
      const response = await api.patch(`/titulos/${id}/kanban-etapa`, { kanbanEtapaId });
      return (handleApiResponse(response) as any).titulo;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getFeedbacksByTitulo(id: string) {
    try {
      const response = await api.get(`/titulos/${id}/feedbacks`);
      return (handleApiResponse(response) as any).feedbacks;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getEstatisticas(): Promise<Estatisticas> {
    try {
      const response = await api.get('/titulos/estatisticas');
      return (handleApiResponse(response) as any).estatisticas;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async distribuir(titulosIds: string[]): Promise<void> {
    try {
      const response = await api.post('/titulos/distribuir', { titulosIds });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getDistribuicao(): Promise<DistribuicaoOperador[]> {
    try {
      const response = await api.get('/titulos/admin/distribuicao');
      return (handleApiResponse(response) as any).distribuicao;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  // External API Import methods
  async importFromExternalApi(params: { data_inicio?: string; data_fim?: string } = {}): Promise<ExternalImportResult> {
    try {
      // Locações + ERP paginado + procedures: pode passar de 60s (default do axios)
      const response = await api.post('/import/external', params, {
        timeout: 15 * 60 * 1000,
      });
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getExternalApiConfig(): Promise<ExternalApiConfig> {
    try {
      const response = await api.get('/import/external/config');
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  // Configuração completa da API externa
  async getExternalApiFullConfig(): Promise<ExternalApiFullConfig> {
    try {
      const response = await api.get('/config/external-api/config');
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async updateExternalApiConfig(config: Partial<ExternalApiFullConfig>): Promise<ExternalApiFullConfig> {
    try {
      const response = await api.put('/config/external-api/config', config);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async testExternalApiConnection(config: Partial<ExternalApiFullConfig>): Promise<ConnectionTestResult> {
    try {
      const response = await api.post('/config/external-api/test', config);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  // Gerenciamento de jobs
  async getExternalApiJobs(): Promise<ExternalApiJob[]> {
    try {
      const response = await api.get('/config/external-api/jobs');
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async createExternalApiJob(jobData: {
    name: string;
    description: string;
    cronExpression: string;
    enabled?: boolean;
  }): Promise<ExternalApiJob> {
    try {
      const response = await api.post('/config/external-api/jobs', jobData);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async updateExternalApiJob(jobId: string, updates: {
    name?: string;
    description?: string;
    cronExpression?: string;
    enabled?: boolean;
  }): Promise<ExternalApiJob> {
    try {
      const response = await api.put(`/config/external-api/jobs/${jobId}`, updates);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async deleteExternalApiJob(jobId: string): Promise<void> {
    try {
      const response = await api.delete(`/config/external-api/jobs/${jobId}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async executeExternalApiJob(jobId: string): Promise<{
    success: boolean;
    message: string;
    executedAt: Date;
    duration: number;
    dataProcessed?: number;
    errors?: string[];
  }> {
    try {
      const response = await api.post(`/config/external-api/jobs/${jobId}/start`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async stopExternalApiJob(jobId: string): Promise<ExternalApiJob> {
    try {
      const response = await api.post(`/config/external-api/jobs/${jobId}/stop`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  /** Teste FlyStart SQL (admin): somente SELECT validado no servidor. Retorno bruto do ERP. */
  async testFlystartSqlQuery(sql: string): Promise<unknown> {
    try {
      const response = await api.post<{
        success: boolean;
        data?: unknown;
        error?: string;
        message?: string;
      }>('/config/external-api/flystart-sql-test', { sql });
      if (!response.data.success) {
        throw new Error(
          response.data.error ?? response.data.message ?? 'Erro ao executar consulta'
        );
      }
      return response.data.data as unknown;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        const d = error.response?.data as { error?: string; message?: string } | undefined;
        throw new Error(d?.error ?? d?.message ?? error.message);
      }
      throw error instanceof Error ? error : new Error('Erro desconhecido');
    }
  },
};
